/**
 * NotificationService — client side of push notifications.
 *
 * Wraps @capacitor-firebase/messaging. The Firebase Messaging SDK is used on
 * BOTH iOS and Android, so getToken() returns an FCM registration token on
 * both — the server's single firebase-admin send path can reach either.
 *
 * Permission philosophy (the whole reason this is a service, not two lines at
 * boot): we NEVER fire the OS prompt cold. iOS gives exactly one shot at it; a
 * denial is near-permanent. So:
 *   - On boot / auth we ONLY check the existing permission. If already granted,
 *     we silently fetch + register the token. We never prompt here.
 *   - The OS prompt is fired ONLY from requestPermissionAndRegister(), which the
 *     soft-ask priming flow calls after the user taps "Enable" on our own UI.
 *
 * Web is a no-op for now (browser push needs a VAPID key + service worker; out
 * of scope for the beta). Native-only via Capacitor.isNativePlatform().
 *
 * IMPORTANT (Capacitor proxy gotcha): a Capacitor plugin object intercepts
 * EVERY property access as a native method call. So you must never let the
 * plugin flow through `await` / an async function's return value — the runtime
 * probes `.then` on it (thenable check), which Capacitor turns into a native
 * `then()` call → "FirebaseMessaging.then() is not implemented on android".
 * That is why we use a STATIC import and call methods directly; we only ever
 * await the RESULT of a method call (a real promise), never the plugin itself.
 */

import { Capacitor } from '@capacitor/core';
import { FirebaseMessaging } from '@capacitor-firebase/messaging';

export type PushPermission = 'granted' | 'denied' | 'prompt';

export interface NotificationServiceDeps {
  /** Send the FCM token to the server (NetworkManager.registerPushToken). */
  registerToken: (token: string, platform: 'ios' | 'android' | 'web') => void;
  unregisterToken: (token: string) => void;
  /** Route a tapped notification's deep link (streetdice://...). */
  onDeepLink?: (url: string) => void;
}

class NotificationServiceImpl {
  private deps: NotificationServiceDeps | null = null;
  private currentToken: string | null = null;
  private listenersBound = false;
  private channelEnsured = false;

  /** Wire dependencies. Call once at boot. Does NOT prompt or register. */
  init(deps: NotificationServiceDeps): void {
    this.deps = deps;
    this.ensureChannel();
  }

  /**
   * Create the Android notification channel that drives WhatsApp-style heads-up
   * banners. Android 8+ takes a notification's importance from its CHANNEL, not
   * the message — without a high-importance channel an FCM push only updates the
   * badge. importance 5 (MAX) = banner + sound. id must match the server's
   * FCM `channelId` and the manifest default_notification_channel_id. Channel
   * creation needs no permission and is idempotent, so it's safe at boot.
   * (iOS ignores this — its presentation is driven by the system + entitlements.)
   */
  private ensureChannel(): void {
    if (this.channelEnsured || !this.supported() || Capacitor.getPlatform() !== 'android') return;
    this.channelEnsured = true;
    void FirebaseMessaging.createChannel({
      id: 'sdl_messages',
      name: 'Messages & Bonuses',
      description: 'Crew messages and daily bonus reminders',
      importance: 5,
      visibility: 1,
      vibration: true,
      lights: true,
    } as any).catch((err: unknown) => {
      console.warn('[NotificationService] createChannel failed:', (err as Error)?.message);
    });
  }

  private supported(): boolean {
    return Capacitor.isNativePlatform();
  }

  private platform(): 'ios' | 'android' | 'web' {
    const p = Capacitor.getPlatform();
    return p === 'ios' || p === 'android' ? p : 'web';
  }

  /** Current OS permission state, normalized. 'prompt' = not yet asked. */
  async getPermission(): Promise<PushPermission> {
    if (!this.supported()) return 'denied';
    try {
      const { receive } = await FirebaseMessaging.checkPermissions();
      return this.normalize(receive);
    } catch (err) {
      console.warn('[NotificationService] checkPermissions failed:', (err as Error)?.message);
      return 'denied';
    }
  }

  private normalize(receive: string): PushPermission {
    if (receive === 'granted') return 'granted';
    if (receive === 'denied') return 'denied';
    return 'prompt'; // 'prompt' | 'prompt-with-rationale' | anything else
  }

  /**
   * Boot/auth path: if permission is ALREADY granted, fetch + register the
   * token silently. NEVER prompts. Safe to call on every auth:success.
   */
  async syncIfAuthorized(): Promise<void> {
    if (!this.supported()) return;
    if ((await this.getPermission()) === 'granted') {
      await this.fetchAndRegister();
    }
  }

  /**
   * Soft-ask path: the user tapped "Enable" on our priming UI. NOW fire the OS
   * prompt and, if granted, fetch + register. Returns the resulting state so
   * the caller can update the toggle / show a settings hint on denial.
   */
  async requestPermissionAndRegister(): Promise<PushPermission> {
    if (!this.supported()) return 'denied';
    try {
      const { receive } = await FirebaseMessaging.requestPermissions();
      const state = this.normalize(receive);
      if (state === 'granted') await this.fetchAndRegister();
      return state;
    } catch (err) {
      console.warn('[NotificationService] requestPermissions failed:', (err as Error)?.message);
      return 'denied';
    }
  }

  private async fetchAndRegister(): Promise<void> {
    if (!this.supported() || !this.deps) return;
    this.bindListeners();
    try {
      const { token } = await FirebaseMessaging.getToken();
      if (token) {
        this.currentToken = token;
        this.deps.registerToken(token, this.platform());
      }
    } catch (err) {
      console.warn('[NotificationService] getToken failed:', (err as Error)?.message);
    }
  }

  /** Bind token-refresh + tap handlers once. Fire-and-forget (don't await the
   *  listener handle — see the proxy gotcha at the top of this file). */
  private bindListeners(): void {
    if (this.listenersBound || !this.supported()) return;
    this.listenersBound = true;

    // Token rotates (reinstall, restore) — re-register the new one.
    void FirebaseMessaging.addListener('tokenReceived', (e: { token?: string }) => {
      if (e?.token && e.token !== this.currentToken) {
        this.currentToken = e.token;
        this.deps?.registerToken(e.token, this.platform());
      }
    });

    // User tapped the notification — route its deep link.
    void FirebaseMessaging.addListener('notificationActionPerformed', (e: any) => {
      const data = e?.notification?.data ?? {};
      const url = data.deepLink || data.deeplink;
      if (url && this.deps?.onDeepLink) this.deps.onDeepLink(String(url));
    });
  }

  /**
   * Settings toggle ON: an explicit user action, so prompting IS appropriate
   * here (unlike the boot path). Prompts if needed, then registers.
   */
  async enable(): Promise<PushPermission> {
    return this.requestPermissionAndRegister();
  }

  /**
   * Settings toggle OFF: the in-app mute. Tells the server to drop this token so
   * pushes stop, but keeps it locally (and keeps the OS permission) so flipping
   * back on re-registers instantly without another prompt.
   */
  async disable(): Promise<void> {
    if (this.currentToken) this.deps?.unregisterToken(this.currentToken);
  }

  /**
   * Logout: stop this device receiving the previous account's pushes. Tells the
   * server to drop the token (synchronous emit), then deletes it locally so a
   * fresh login mints a new one bound to the new account. Token deletion is
   * best-effort and must never block logout (caller time-boxes this).
   */
  async clearOnLogout(): Promise<void> {
    const token = this.currentToken;
    if (token) this.deps?.unregisterToken(token);
    this.currentToken = null;
    if (!this.supported()) return;
    try {
      await FirebaseMessaging.deleteToken();
    } catch {
      /* best-effort */
    }
  }
}

/** App-wide singleton. */
export const notificationService = new NotificationServiceImpl();
